import { logEvent } from "@/analytics/analytics"
import { Button } from "@/components/ui/button"
import { FieldGroup } from "@/components/ui/field-group"
import { FormActions } from "@/components/ui/form-actions"
import { RhfField } from "@/components/ui/rhf-field"
import { useToast } from "@/components/ui/use-toast"
import { signInPath } from "@/org-admins//org-admins-paths"
import { webLinks } from "@/util/environment"
import { displayValidationErrors } from "@/util/validations"
import { useMutation } from "@apollo/client"
import { zodResolver } from "@hookform/resolvers/zod"
import React from "react"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { Link } from "react-router-dom"
import { createOrgAdminUserMutation } from "./api"
import {
  OrgAdminCreateAccountFormValues,
  orgAdminCreateAccountSchema,
} from "./schema"

const CreateAccountForm = ({
  memberInviteToken,
  onCreationSuccess,
}: {
  memberInviteToken: string
  onCreationSuccess: () => void
}) => {
  const [execCreateUser] = useMutation(createOrgAdminUserMutation)
  const { toast } = useToast()
  const defaultValues: OrgAdminCreateAccountFormValues = {
    name: "",
    email: "",
    password: "",
    passwordConfirmation: "",
  }

  const methods = useForm<OrgAdminCreateAccountFormValues>({
    resolver: zodResolver(orgAdminCreateAccountSchema),
    defaultValues,
  })
  const { control, formState, setError } = methods

  const onSubmit: SubmitHandler<OrgAdminCreateAccountFormValues> = async (
    values,
  ) => {
    logEvent("Create org admin user")

    // Simple captcha / honeypot
    if (values.name.length > 0) return

    try {
      const { data } = await execCreateUser({
        variables: {
          input: {
            memberInviteToken: memberInviteToken,
            email: values.email,
            password: values.password,
          },
        },
      })

      if (
        data?.createOrgAdminUser.errors &&
        data?.createOrgAdminUser.errors.length > 0
      ) {
        throw data?.createOrgAdminUser.errors
      }

      onCreationSuccess()
    } catch (e: any) {
      console.error(e)
      displayValidationErrors({
        recordType: "user",
        errors: e,
        setError,
        toast,
        validationErrorTitle: "Account was not created.",
      })
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <FieldGroup>
          <RhfField
            name="email"
            control={control}
            placeholder="Email address"
            label="Email Address"
          />
          <div className="text-neutral-400 mb-4">
            Use the email address that was set as the organization
            administrator.
          </div>
        </FieldGroup>
        <FieldGroup>
          <RhfField
            name="password"
            control={control}
            type="password"
            placeholder="Password"
            label="Password"
          />
        </FieldGroup>
        <FieldGroup>
          <RhfField
            name="passwordConfirmation"
            control={control}
            type="password"
            placeholder="Confirm Password"
            label="Confirm Password"
          />
        </FieldGroup>
        <RhfField name="name" control={control} hidden={true} />
        <FormActions>
          <Button
            type="submit"
            variant="default"
            disabled={formState.isSubmitting}
          >
            Create Account
          </Button>
        </FormActions>

        <div className="text-sm text-neutral-400 max-w-[270px] mx-auto mt-8">
          Already have an account?{" "}
          <Link className="underline" to={signInPath({})}>
            Sign In
          </Link>
        </div>

        <div className="text-sm text-neutral-400 max-w-[270px] mx-auto mt-4">
          By creating an account you agree with our{" "}
          <a
            href={webLinks.privacyPolicy}
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Privacy Policy
          </a>{" "}
          and{" "}
          <a
            href={webLinks.termsOfUse}
            target="_blank"
            rel="noreferrer"
            className="underline"
          >
            Terms of Use
          </a>
          .
        </div>
      </form>
    </FormProvider>
  )
}

export default CreateAccountForm
